import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useAppointments, useDepartments, useDoctors, useSettings } from '../../services/httpServices/queries';
import { SearchableSelect } from './searchable-select';
import { DatePicker } from './DatePicker';
import { joinFullName } from '../../utils/patientName';
import { formatDateWithWeekday } from '../../utils/dateFormat';
import { X, Printer, Loader2, AlertCircle, Calendar } from 'lucide-react';

const WEEKDAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

interface SessionRow {
  id: string;
  appointment: any;
  serial: number;
}

interface SessionGroup {
  key: string;
  title: string;
  from?: string;
  to?: string;
  rows: SessionRow[];
}

const PRINT_LIMIT = 1000;

/**
 * Usable height of one A4 page at 96 dpi (297mm − 2×12mm margins), used to decide
 * how many blank rows are needed to fill the sheet.
 */
const A4_CONTENT_HEIGHT = 1032;
const MAX_BLANK_ROWS = 60;

/** "H:mm" / "HH:mm" normalized to a comparable zero-padded string. */
function normalizeTime(time?: string): string {
  if (!time) return '';
  const [hours, minutes] = String(time).split(':');
  return `${String(hours || '0').padStart(2, '0')}:${String(minutes ?? '00').padStart(2, '0')}`;
}

function toDateKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`;
}

function weekdayFor(dateKey: string): string {
  if (!dateKey) return '';
  return WEEKDAYS[new Date(`${dateKey}T00:00:00`).getDay()];
}

/** Consultation time ranges for the doctor on the given weekday (may be malformed). */
function sessionsFor(
  doctor: any,
  dateKey: string,
): Array<{ title?: string; from?: string; to?: string }> {
  const availability = doctor?.consultationAvailability;
  if (!Array.isArray(availability)) return [];
  const target = weekdayFor(dateKey).toLowerCase();
  const entry =
    availability.find((item: any) => String(item?.day || '').toLowerCase() === target) ??
    availability.find((item: any) => String(item?.day || '').toLowerCase() === 'everyday');
  return Array.isArray(entry?.timeRanges) ? entry.timeRanges : [];
}

/** Consultation session (named time range) the appointment time falls into. */
function findSessionFor(
  doctor: any,
  dateKey: string,
  time?: string,
): { title: string; from: string; to: string } | null {
  const ranges = sessionsFor(doctor, dateKey);
  if (!ranges.length) return null;

  const current = normalizeTime(time);
  const match = ranges.find(
    (range) =>
      range?.from &&
      range?.to &&
      current >= normalizeTime(range.from) &&
      current < normalizeTime(range.to),
  );
  if (!match?.from || !match?.to) return null;
  return { title: (match.title || '').trim(), from: match.from, to: match.to };
}

/** Stable identity for a consultation session, shared by grouping and filtering. */
function sessionKeyOf(session: { title?: string; from: string; to: string } | null): string {
  if (!session) return '__all__';
  return `${normalizeTime(session.from)}|${normalizeTime(session.to)}|${(session.title || '').trim()}`;
}

/** Prefers the sanitized availability from the doctors list when the joined row lacks it. */
function withAvailability(
  doctor: any,
  doctorId: string | undefined,
  doctorsById: Map<string, any>,
): any {
  const known = doctorId ? doctorsById.get(doctorId) : undefined;
  if (doctor?.consultationAvailability || !known?.consultationAvailability) return doctor;
  return { ...doctor, consultationAvailability: known.consultationAvailability };
}

/**
 * Groups the day's appointments by consultation session (Morning / Evening …),
 * ordered by session start time. Appointments that fall outside every configured
 * session are collected in a single "Unscheduled" group. Serial numbers restart
 * at 1 for every session.
 */
function buildSessionGroups(
  appointments: any[],
  dateKey: string,
  doctorsById: Map<string, any>,
): SessionGroup[] {
  const groups = new Map<string, SessionGroup>();
  const order: string[] = [];

  const sorted = [...appointments].sort((a, b) => {
    const byTime = normalizeTime(a?.time).localeCompare(normalizeTime(b?.time));
    if (byTime !== 0) return byTime;
    return String(a?.doctor?.name || '').localeCompare(String(b?.doctor?.name || ''));
  });

  for (const appointment of sorted) {
    const doctor = withAvailability(appointment?.doctor, appointment?.doctorId, doctorsById);
    const session = findSessionFor(doctor, dateKey, appointment?.time);
    const key = sessionKeyOf(session);

    let group = groups.get(key);
    if (!group) {
      group = { key, title: session?.title || '', from: session?.from, to: session?.to, rows: [] };
      groups.set(key, group);
      order.push(key);
    }
    group.rows.push({ id: appointment.id, appointment, serial: 0 });
  }

  const result = order.map((key) => groups.get(key)!);
  result.sort((a, b) => {
    if (!a.from && !b.from) return 0;
    if (!a.from) return 1;
    if (!b.from) return -1;
    const byStart = normalizeTime(a.from).localeCompare(normalizeTime(b.from));
    return byStart !== 0 ? byStart : a.title.localeCompare(b.title);
  });

  result.forEach((group) => {
    group.rows.forEach((row, index) => {
      row.serial = index + 1;
    });
  });

  return result;
}

function sessionTitle(group: SessionGroup): string {
  if (group.title) return group.title;
  if (group.from) return 'Consultation';
  return 'Unscheduled / Other';
}

function formatDoctorName(name?: string): string {
  const value = (name || '').trim();
  if (!value) return '—';
  return /^dr\.?\s/i.test(value) ? value : `Dr. ${value}`;
}

function patientName(appointment: any): string {
  const patient = appointment?.patient;
  if (patient) {
    const name = joinFullName(patient);
    if (name) return name;
  }
  return appointment?.patientName?.trim() || 'Walk-in patient';
}

function patientRegistration(appointment: any): string {
  return appointment?.patient?.registrationNumber || '';
}

function patientMobile(appointment: any): string {
  return appointment?.patient?.mobileNumber || appointment?.patientMobile || '';
}

/**
 * Appointment "Type" exactly as saved from the booking form's
 * "Appointment Details → Type" field (New / Follow-up / Emergency / Procedure /
 * Report Review). No walk-in override — the type is whatever was booked.
 */
function appointmentTypeLabel(appointment: any): string {
  return appointment?.appointmentType?.trim() || '—';
}

/**
 * Self-contained styles for the print sheet. Used both for the on-screen
 * preview and for the standalone print window, so the two always match.
 */
const SHEET_CSS = `
.aps-sheet { color:#111827; font-family:Arial,Helvetica,sans-serif; font-size:12px; background:#ffffff; padding:16px; }
.aps-sheet * { box-sizing:border-box; }
.aps-header { text-align:center; border-bottom:2px solid #111827; padding-bottom:8px; margin-bottom:8px; }
.aps-hospital { font-size:20px; font-weight:700; }
.aps-title { font-size:13px; font-weight:600; margin-top:2px; letter-spacing:1px; text-transform:uppercase; }
.aps-meta { font-size:11px; color:#374151; margin-top:5px; display:flex; justify-content:center; gap:18px; flex-wrap:wrap; }
.aps-session { margin-top:14px; }
.aps-session-title { display:flex; align-items:baseline; justify-content:space-between; gap:8px; font-size:12px; font-weight:700; background:#e5e7eb; padding:5px 8px; border-left:3px solid #0f766e; page-break-after:avoid; }
.aps-session-count { font-weight:400; font-size:11px; color:#374151; }
.aps-table { width:100%; border-collapse:collapse; margin-top:6px; }
.aps-table th, .aps-table td { border:1px solid #d1d5db; padding:6px 8px; text-align:left; vertical-align:top; }
.aps-table td { height:26px; }
.aps-table tr { page-break-inside:avoid; }
.aps-table thead { display:table-header-group; }
.aps-table th { background:#f9fafb; font-size:10px; text-transform:uppercase; letter-spacing:.4px; text-align:center; }
.aps-serial { width:42px; font-weight:700; }
.aps-check { width:58px; }
.aps-mobile { width:140px; }
.aps-type { width:70px; }
.aps-table th.aps-serial, .aps-table td.aps-serial { text-align:center; }
.aps-table th.aps-check, .aps-table td.aps-check { text-align:center; vertical-align:middle; }
.aps-table th.aps-type, .aps-table td.aps-type { text-align:center; }
.aps-checkbox { display:inline-block; width:16px; height:16px; border:1.5px solid #111827; border-radius:2px; }
.aps-note { width:20%; color:#6b7280; }
.aps-patient-name { font-weight:600; }
.aps-patient-reg { font-size:10px; color:#6b7280; }
.aps-empty { padding:32px; text-align:center; color:#6b7280; }
.aps-footer { margin-top:18px; padding-top:6px; border-top:1px solid #d1d5db; font-size:10px; color:#6b7280; display:flex; justify-content:space-between; }
`;

interface AppointmentPrintModalProps {
  onClose: () => void;
  /** Pre-select a doctor (UUID) when the modal is opened from a doctor page. */
  initialDoctorId?: string;
}

export function AppointmentPrintModal({ onClose, initialDoctorId = '' }: AppointmentPrintModalProps) {
  const [date, setDate] = useState(() => toDateKey(new Date()));
  const [departmentId, setDepartmentId] = useState('');
  const [doctorId, setDoctorId] = useState(initialDoctorId);
  const [sessionKey, setSessionKey] = useState('');
  const [includeCancelled, setIncludeCancelled] = useState(false);
  const [blankRows, setBlankRows] = useState(0);
  const sheetRef = useRef<HTMLDivElement>(null);

  const { data: doctorsData } = useDoctors(1, 500, { status: 'active' });
  const { data: departmentsData } = useDepartments();
  const { data: settingsData } = useSettings();

  const { data, isLoading, isError, isFetching } = useAppointments(1, PRINT_LIMIT, undefined, {
    date: date || undefined,
    doctorId: doctorId || undefined,
    departmentId: departmentId || undefined,
  });

  const doctors = useMemo(() => doctorsData?.data || [], [doctorsData]);
  const departments = useMemo(() => departmentsData?.data || [], [departmentsData]);
  const doctorsById = useMemo(
    () => new Map(doctors.map((doctor) => [doctor.id, doctor])),
    [doctors],
  );

  // Department -> Doctor: keep only the doctors belonging to the chosen department.
  const doctorsInDepartment = useMemo(() => {
    if (!departmentId) return doctors;
    return doctors.filter(
      (doctor) =>
        doctor.departmentId === departmentId ||
        doctor.department?.id === departmentId ||
        (doctor.departments || []).some((entry) => entry.id === departmentId),
    );
  }, [doctors, departmentId]);

  const hospitalName =
    settingsData?.data?.find((setting) => setting.key === 'hospital_name_en')?.value?.trim() ||
    settingsData?.data?.find((setting) => setting.key === 'hospital_name')?.value?.trim() ||
    'Hospital';

  const departmentOptions = useMemo(
    () => [
      { value: '', label: 'All departments' },
      ...departments.map((department) => ({
        value: department.id,
        label: department.code ? `${department.name} (${department.code})` : department.name,
      })),
    ],
    [departments],
  );

  const doctorOptions = useMemo(
    () => [
      { value: '', label: 'All doctors' },
      ...doctorsInDepartment.map((doctor) => ({
        value: doctor.id,
        label: doctor.designation?.name ? `${doctor.name} · ${doctor.designation.name}` : doctor.name,
      })),
    ],
    [doctorsInDepartment],
  );

  const statusFiltered = useMemo(() => {
    const rows = data?.data || [];
    return includeCancelled
      ? rows
      : rows.filter((appointment: any) => appointment?.status !== 'CANCELLED');
  }, [data, includeCancelled]);

  // Date -> Session: the sessions that actually occur in the date/department/doctor set.
  const sessionOptions = useMemo(() => {
    const map = new Map<string, { key: string; label: string; from: string }>();
    let hasUnscheduled = false;

    for (const appointment of statusFiltered) {
      const doctor = withAvailability(appointment?.doctor, appointment?.doctorId, doctorsById);
      const session = findSessionFor(doctor, date, appointment?.time);
      if (!session) {
        hasUnscheduled = true;
        continue;
      }
      const key = sessionKeyOf(session);
      if (!map.has(key)) {
        map.set(key, {
          key,
          label: [session.title, `${session.from}–${session.to}`].filter(Boolean).join(' · '),
          from: session.from,
        });
      }
    }

    // Appointments outside every configured session still need to be printable.
    if (hasUnscheduled) {
      map.set('__all__', { key: '__all__', label: 'Unscheduled / Other', from: '99:99' });
    }

    return Array.from(map.values()).sort((a, b) =>
      normalizeTime(a.from).localeCompare(normalizeTime(b.from)),
    );
  }, [statusFiltered, date, doctorsById]);

  const sessionSelectOptions = useMemo(
    () => [
      { value: '', label: 'Select session' },
      ...sessionOptions.map((session) => ({ value: session.key, label: session.label })),
    ],
    [sessionOptions],
  );

  const appointments = useMemo(() => {
    if (!sessionKey) return statusFiltered;
    return statusFiltered.filter((appointment: any) => {
      const doctor = withAvailability(appointment?.doctor, appointment?.doctorId, doctorsById);
      return sessionKeyOf(findSessionFor(doctor, date, appointment?.time)) === sessionKey;
    });
  }, [statusFiltered, sessionKey, date, doctorsById]);

  const sessionGroups = useMemo(
    () => buildSessionGroups(appointments, date, doctorsById),
    [appointments, date, doctorsById],
  );

  // Fill the rest of the printed page with blank ruled rows for manual entries.
  useEffect(() => {
    const sheet = sheetRef.current;
    if (!sheet || !sessionKey || sessionGroups.length === 0) {
      if (blankRows !== 0) setBlankRows(0);
      return;
    }

    // Measure the blank rows that are already rendered so the real content height
    // can be recovered exactly (a blank row is shorter than a data row that has a
    // registration subtitle, so scaling by the data-row height would drift).
    const blankEls = sheet.querySelectorAll<HTMLTableRowElement>('tr.aps-blank-row');
    let blankHeight = 0;
    blankEls.forEach((el) => {
      blankHeight += el.getBoundingClientRect().height;
    });

    const style = getComputedStyle(sheet);
    const sheetPadding =
      (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0);

    const contentHeight = sheet.getBoundingClientRect().height - sheetPadding - blankHeight;
    if (contentHeight <= 0) return;

    // Prefer an actual blank row's height; a data row is only a first-pass estimate.
    const firstRow = sheet.querySelector('table.aps-table tbody tr');
    const rowHeight =
      blankEls[0]?.getBoundingClientRect().height ||
      firstRow?.getBoundingClientRect().height ||
      38;

    const pages = Math.max(1, Math.ceil(contentHeight / A4_CONTENT_HEIGHT));
    const usedOnLastPage = contentHeight - (pages - 1) * A4_CONTENT_HEIGHT;
    const remaining = A4_CONTENT_HEIGHT - usedOnLastPage;
    const count = Math.max(0, Math.min(MAX_BLANK_ROWS, Math.floor(remaining / rowHeight) - 1));

    if (count !== blankRows) setBlankRows(count);
  }, [sessionGroups, sessionKey, blankRows]);

  const totalPatients = appointments.length;
  const selectedDepartmentName = departments.find((department) => department.id === departmentId)?.name;
  const selectedDoctorName = doctorId
    ? doctorsInDepartment.find((doctor) => doctor.id === doctorId)?.name ||
      statusFiltered.find(
        (appointment: any) => (appointment?.doctorId || appointment?.doctor?.id) === doctorId,
      )?.doctor?.name
    : undefined;
  // When a single doctor is selected every row shows the same name, so the
  // doctor is stated once in the header instead of repeated on every row.
  const showDoctorColumn = !doctorId;

  const handleDepartmentChange = (value: string) => {
    setDepartmentId(value);
    setDoctorId('');
    setSessionKey('');
  };

  const handleDoctorChange = (value: string) => {
    setDoctorId(value);
    setSessionKey('');
  };

  const handleDateChange = (value: string) => {
    setDate(value);
    setSessionKey('');
  };

  const handlePrint = () => {
    const sheet = document.getElementById('appointment-print-sheet');
    if (!sheet) return;

    const printWindow = window.open('', '_blank', 'width=1000,height=800');
    if (!printWindow) {
      toast.error('Please allow pop-ups to print the appointment list');
      return;
    }

    printWindow.document.open();
    printWindow.document.write(
      `<!doctype html><html><head><meta charset="utf-8" /><title>Appointment List</title>` +
        `<style>${SHEET_CSS}` +
        `@page{size:A4;margin:12mm;}` +
        `@media print{body{margin:0;}.aps-sheet{padding:0;}}` +
        `</style></head><body>${sheet.outerHTML}</body></html>`,
    );
    printWindow.document.close();
    printWindow.focus();
    printWindow.onafterprint = () => printWindow.close();
    window.setTimeout(() => {
      printWindow.focus();
      printWindow.print();
    }, 350);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="bg-surface border border-border rounded-xl shadow-xl w-full max-w-4xl max-h-[92vh] flex flex-col"
        onClick={(event) => event.stopPropagation()}
      >
        <style>{SHEET_CSS}</style>

        {/* Header */}
        <div className="flex items-start justify-between gap-3 p-4 border-b border-border">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Printer size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-text">Print Appointment List</h2>
              <p className="text-sm text-text-muted">
                Grouped by consultation session. Each session&apos;s serial starts at 1.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-background text-text-muted"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Filters */}
        <div className="p-4 border-b border-border space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-medium text-text-muted mb-1">Department</label>
              <SearchableSelect
                value={departmentId}
                onChange={handleDepartmentChange}
                options={departmentOptions}
                placeholder="All departments"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-text-muted mb-1">Doctor</label>
              <SearchableSelect
                value={doctorId}
                onChange={handleDoctorChange}
                options={doctorOptions}
                placeholder="All doctors"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-text-muted mb-1">Date</label>
              <DatePicker value={date} onChange={handleDateChange} />
            </div>
            <div>
              <label className="block text-xs font-medium text-text-muted mb-1">Session</label>
              <SearchableSelect
                value={sessionKey}
                onChange={setSessionKey}
                options={sessionSelectOptions}
                placeholder="Select session"
              />
            </div>
          </div>
          <p className="text-xs text-text-muted">
            Department narrows the doctor list; pick a date, then choose a session to show that
            session&apos;s list below.
          </p>

          <label className="inline-flex items-center gap-2 text-sm text-text cursor-pointer">
            <input
              type="checkbox"
              checked={includeCancelled}
              onChange={(event) => setIncludeCancelled(event.target.checked)}
              className="w-4 h-4 accent-primary"
            />
            Include cancelled appointments
          </label>
        </div>

        {/* Preview */}
        <div className="flex-1 overflow-y-auto bg-background p-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : isError ? (
            <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
              <AlertCircle size={18} />
              <span>Failed to load appointments for printing.</span>
            </div>
          ) : !sessionKey ? (
            <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
              <div className="p-3 rounded-full bg-surface border border-border">
                <Calendar className="w-7 h-7 text-text-muted" />
              </div>
              <p className="text-sm text-text-muted">
                Select a session to preview and print the list.
              </p>
              {sessionOptions.length === 0 && (
                <p className="text-xs text-text-muted">
                  No appointments found for the current date / department / doctor.
                </p>
              )}
            </div>
          ) : (
            <div className="mx-auto max-w-[210mm] shadow-sm border border-border rounded-lg overflow-hidden">
              <div id="appointment-print-sheet" className="aps-sheet" ref={sheetRef}>
                <div className="aps-header">
                  <div className="aps-hospital">{hospitalName}</div>
                  <div className="aps-title">Appointment List</div>
                  <div className="aps-meta">
                    <span>
                      <Calendar size={12} className="inline -mt-0.5 mr-1" />
                      Date: {formatDateWithWeekday(date)}
                    </span>
                    {selectedDepartmentName && <span>Department: {selectedDepartmentName}</span>}
                    {selectedDoctorName && (
                      <span>Doctor: {formatDoctorName(selectedDoctorName)}</span>
                    )}
                    <span>Total patients: {totalPatients}</span>
                  </div>
                </div>

                {sessionGroups.length === 0 ? (
                  <div className="aps-empty">No appointments to print for this selection.</div>
                ) : (
                  sessionGroups.map((group, groupIndex) => {
                    // Only the final table is padded so the printed sheet ends on a
                    // full page of ruled blank rows for manual entries.
                    const fillerRows =
                      groupIndex === sessionGroups.length - 1 ? blankRows : 0;

                    return (
                      <div key={group.key} className="aps-session">
                        <div className="aps-session-title">
                          <span>
                            {sessionTitle(group)}
                            {group.from && group.to ? ` (${group.from}–${group.to})` : ''}
                          </span>
                          <span className="aps-session-count">
                            {group.rows.length} patient{group.rows.length === 1 ? '' : 's'}
                          </span>
                        </div>

                        <table className="aps-table">
                          <thead>
                            <tr>
                              <th className="aps-serial">#</th>
                              <th>Patient</th>
                              {showDoctorColumn && <th>Doctor</th>}
                              <th className="aps-mobile">Mobile</th>
                              <th className="aps-type">Type</th>
                              <th className="aps-check">Check</th>
                              <th className="aps-note">Note</th>
                            </tr>
                          </thead>
                          <tbody>
                            {group.rows.map((row) => (
                              <tr key={row.id}>
                                <td className="aps-serial">{row.serial}</td>
                                <td>
                                  <div className="aps-patient-name">
                                    {patientName(row.appointment)}
                                  </div>
                                  {patientRegistration(row.appointment) && (
                                    <div className="aps-patient-reg">
                                      {patientRegistration(row.appointment)}
                                    </div>
                                  )}
                                </td>
                                {showDoctorColumn && (
                                  <td>{formatDoctorName(row.appointment?.doctor?.name)}</td>
                                )}
                                <td className="aps-mobile">{patientMobile(row.appointment) || '—'}</td>
                                <td className="aps-type">{appointmentTypeLabel(row.appointment)}</td>
                                <td className="aps-check">
                                  <span className="aps-checkbox" />
                                </td>
                                <td className="aps-note">{row.appointment?.notes || ''}</td>
                              </tr>
                            ))}
                            {Array.from({ length: fillerRows }).map((_, index) => (
                              <tr key={`blank-${index}`} className="aps-blank-row" aria-hidden="true">
                                <td className="aps-serial">{group.rows.length + index + 1}</td>
                                <td />
                                {showDoctorColumn && <td />}
                                <td className="aps-mobile" />
                                <td className="aps-type" />
                                <td className="aps-check">
                                  <span className="aps-checkbox" />
                                </td>
                                <td className="aps-note" />
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  })
                )}

                <div className="aps-footer">
                  <span>{hospitalName}</span>
                  <span>Doctor&apos;s assistant use — tick the Check box after manual verification.</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 p-4 border-t border-border">
          <span className="text-xs text-text-muted">
            {!sessionKey
              ? 'Select a session to preview the list'
              : isFetching && !isLoading
                ? 'Refreshing…'
                : `${sessionGroups.length} session(s) · ${totalPatients} patient(s)`}
          </span>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-text-muted hover:text-text transition-colors"
            >
              Close
            </button>
            <button
              onClick={handlePrint}
              disabled={isLoading || !sessionKey || sessionGroups.length === 0}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium disabled:opacity-50"
            >
              <Printer size={16} />
              Print
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
